import { Component, TemplateRef, ViewChild, inject, input, linkedSignal, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { Subject } from 'rxjs';

import { FormComponent } from '@camelot/form-basic';
import { CheckboxComponent } from '@camelot/form-input';
import { CamAbstractInput, InputCheckBox, InputUploadValue } from '@camelot/form-model';
import { ENotificationCode, LAZY_SERVICE_TOKEN } from '@camelot/notification';
import { KeyValue } from '@camelot/server';
import { CamContactService } from '@camelot/services';
import { TranslatePipe } from '@camelot/translation';
import { LayoutFullPanelComponent, LinkComponent } from '@camelot/ui';
import { WysiswgBlockData, convertBlocksToHtml } from '@camelot/wysiswyg';

import {
  AbstractPanelFormLayout,
  PanelFormLayoutComponent,
} from '../../../../components/panel-form-layout/panel-form-layout.component';
import { ConversationDocumentsPools } from '../../helpers/documents-sections.helper';
import { CamCommunicationsService } from '../../services/communications.service';
import { CommunicationType, ConversationOwnerType } from '../../services/dto/communication';
import { CorrespondentInputDto } from '../../services/dto/post/communication';
import { TemplateVariant } from '../../services/dto/template';
import { CamEmailSendFormService } from '../../services/form/email-send.service';
import { CamCommunicationsTemplatesService } from '../../services/templates.service';
import { CamTranslationCommunication } from '../../translation.service';
import { AttachmentsCardsComponent } from '../attachments-cards/attachments-cards.component';
import { CommunicationTemplateChoiceComponent } from '../templates/choice/choice.component';

@Component({
  selector: 'cam-communication-email-send',
  imports: [
    AttachmentsCardsComponent,
    CheckboxComponent,
    CommunicationTemplateChoiceComponent,
    FormComponent,
    LayoutFullPanelComponent,
    LinkComponent,
    PanelFormLayoutComponent,
    TranslatePipe,
  ],
  templateUrl: './email-send.component.html',
  styleUrl: './email-send.component.scss',
})
export class CamEmailSendComponent extends AbstractPanelFormLayout {
  ownerId = input.required<string>();

  ownerType = input.required<ConversationOwnerType>();

  contactId = input.required<string>();

  subject = input<string>('');

  /** Pièces déjà rattachées à l'entité, cochées à l'ouverture. */
  initialAttachments = input<InputUploadValue[]>([]);

  /** Documents proposés à la sélection, par origine. */
  documentsPools = input<ConversationDocumentsPools>({});

  /** Variables résolues dans le corps d'un modèle, ex. `INVOICE_ID`. */
  templateContext = input<KeyValue[]>([]);

  closeEvent = output<void>();

  @ViewChild('recipientLabelActions', { static: true })
  private _recipientLabelActions!: TemplateRef<void>;

  @ViewChild('bodyLabelActions', { static: true })
  private _bodyLabelActions!: TemplateRef<void>;

  readonly CommunicationType = CommunicationType;
  readonly setBody$ = new Subject<WysiswgBlockData[]>();
  readonly templateChoiceOpen = signal(false);
  readonly ccVisible = signal(false);
  // Hors cam-form pour placer la checkbox au-dessus du bloc pièces jointes, pas parmi les champs email.
  readonly attachmentsEnabled = signal(true);
  readonly attachmentsEnabledInput = new InputCheckBox({
    key: 'attachmentsEnabled',
    label: 'communication.email.attach-documents',
    description: 'communication.email.attach-documents-description',
    value: true,
  });

  public form = signal<CamAbstractInput[]>([]);
  // Re-fournies au bloc quand la checkbox le recrée, et resynchronisées si l'appelant les résout tard.
  readonly attachments = linkedSignal(() => this.initialAttachments());

  private _contactMail = '';

  private readonly _ccVisible$ = toObservable(this.ccVisible);

  private readonly _communicationsService = inject(CamCommunicationsService);
  private readonly _formService = inject(CamEmailSendFormService);
  private readonly _templatesService = inject(CamCommunicationsTemplatesService);
  private readonly _contactService = inject(CamContactService);
  private readonly _notificationService = inject(LAZY_SERVICE_TOKEN);

  constructor() {
    super();
    CamTranslationCommunication.getInstance();
  }

  ngOnInit() {
    this.form.set(
      this._formService.getEmailForm({
        bodyLabelTemplate: this._bodyLabelActions,
        ccVisible$: this._ccVisible$,
        setBody$: this.setBody$,
        contactId: this.contactId(),
        recipientLabelTemplate: this._recipientLabelActions,
        subject: this.subject(),
      })
    );
    this._watchContactMail();
  }

  public onAttachments(attachments: InputUploadValue[]) {
    this.attachments.set(attachments);
  }

  public selectTemplate(variant: TemplateVariant) {
    this.templateChoiceOpen.set(false);

    if (variant?.jsonBodyParsed) {
      this._templatesService
        .previewConversationTemplate$(JSON.stringify(variant.jsonBodyParsed), this.templateContext())
        .subscribe({
          next: value => this.setBody$.next(JSON.parse(value)),
        });
    }
  }

  public validation(data: any): void {
    if (this.requestState.isLoading()) {
      return;
    }
    this.requestState.asked();

    // La checkbox couvre tout le bloc, PDF inclus : décochée, le mail part sans aucune pièce jointe.
    const attachments = this.attachmentsEnabled() ? this.attachments() : [];

    this._communicationsService
      .startOutboundConversation$({
        attachmentUrls: attachments.map(attachment => attachment.url),
        body: convertBlocksToHtml(data['body'] ?? []),
        ccCorrespondents: this._toCorrespondents(data['cc']),
        documentIds: attachments.map(attachment => attachment.id).filter(id => id !== ''),
        ownerId: this.ownerId(),
        ownerType: this.ownerType(),
        subject: data['subject'],
        toCorrespondents: this._toCorrespondents(data['to']),
      })
      .subscribe({
        complete: () => {
          this.requestState.completed();
          this._notificationService.addNotification('notification.common.success', ENotificationCode.success);
          this.closeEvent.emit();
        },
        error: () => {
          this.requestState.completed();
          this._notificationService.addNotification('notification.common.error', ENotificationCode.error);
        },
      });
  }

  private _toCorrespondents(value?: string): CorrespondentInputDto[] {
    if (!value) {
      return [];
    }

    return [{ contactId: value === this._contactMail ? this.contactId() : null, mail: value }];
  }

  private _watchContactMail(): void {
    if (!this.contactId()) {
      return;
    }

    this._registerSubscription(
      this._contactService.fetchContactById$(this.contactId()).subscribe({
        next: contact => (this._contactMail = contact?.mail ?? ''),
      })
    );
  }
}
